import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { z } from 'zod';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { COUNTRIES, DEFAULT_COUNTRY_CODE, isValidCountryCode } from '@/lib/auth/countries';
import { getMyTenant, TENANT_ME_QUERY_KEY, updateMyTenant } from '@/lib/api/tenants';
import {
  createShipFromAddress,
  updateShipFromAddress,
  type ShipFromAddress,
} from '@/lib/api/ship-from-addresses';
import { cn } from '@/lib/utils';

import { OnboardingShell } from './OnboardingShell';
import { StepIndicator } from './StepIndicator';

const ShipFromSchema = z.object({
  line1: z.string().min(1, 'Address line 1 is required').max(200, 'Line 1 is too long'),
  line2: z.string().max(200, 'Line 2 is too long').optional().or(z.literal('')),
  city: z.string().min(1, 'City is required').max(120, 'City is too long'),
  state: z.string().max(120, 'State / region is too long').optional().or(z.literal('')),
  postal_code: z.string().min(1, 'Postal code is required').max(20, 'Postal code is too long'),
  country: z
    .string()
    .min(1, 'Pick a country')
    .refine(isValidCountryCode, 'Pick a supported country'),
});

type ShipFromValues = z.infer<typeof ShipFromSchema>;

function existingAddressToFormValues(
  address: ShipFromAddress | undefined,
  fallbackCountry: string,
): ShipFromValues {
  return {
    line1: address?.line1 ?? '',
    line2: address?.line2 ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    postal_code: address?.postal_code ?? '',
    country: address?.country ?? fallbackCountry,
  };
}

/**
 * Step 2 of 5 — ship-from address. Creates a `ship_from_addresses`
 * row on first save (and PATCHes the tenant to link it as the default)
 * or updates the existing row on subsequent saves. Pre-fills from the
 * tenant's embedded `default_ship_from_address` when present, falling
 * back to the tenant's business country as the country default.
 */
export function Step2ShipFrom() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const tenantQuery = useQuery({
    queryKey: TENANT_ME_QUERY_KEY,
    queryFn: async () => {
      const result = await getMyTenant();
      if (result.isErr()) throw new Error(result.error.message);
      return result.value;
    },
  });
  const [topError, setTopError] = useState<string | null>(null);

  const fallbackCountry = tenantQuery.data?.country ?? DEFAULT_COUNTRY_CODE;
  const existingAddress = tenantQuery.data?.default_ship_from_address;

  const form = useForm<ShipFromValues>({
    resolver: zodResolver(ShipFromSchema),
    mode: 'onChange',
    defaultValues: existingAddressToFormValues(existingAddress, fallbackCountry),
  });

  // Reset the form when the tenant query resolves (or refetches) so
  // the form reflects the current cached address / country.
  useEffect(() => {
    if (tenantQuery.data) {
      form.reset(
        existingAddressToFormValues(
          tenantQuery.data.default_ship_from_address,
          tenantQuery.data.country ?? DEFAULT_COUNTRY_CODE,
        ),
      );
    }
  }, [tenantQuery.data, form]);

  const submit = form.handleSubmit(async (values) => {
    setTopError(null);
    const payload = {
      line1: values.line1,
      line2: values.line2?.length ? values.line2 : undefined,
      city: values.city,
      state: values.state?.length ? values.state : undefined,
      postal_code: values.postal_code,
      country: values.country,
    };

    const existingId = tenantQuery.data?.default_ship_from_address_id;
    if (existingId) {
      const updated = await updateShipFromAddress(existingId, payload);
      if (updated.isErr()) {
        surfaceError(updated.error.kind === 'unauthorised');
        return;
      }
    } else {
      const created = await createShipFromAddress(payload);
      if (created.isErr()) {
        surfaceError(created.error.kind === 'unauthorised');
        return;
      }
      const linked = await updateMyTenant({
        default_ship_from_address_id: created.value.address_id,
      });
      if (linked.isErr()) {
        surfaceError(linked.error.kind === 'unauthorised');
        return;
      }
    }
    await qc.invalidateQueries({ queryKey: TENANT_ME_QUERY_KEY });
    void navigate('/app/onboarding/step/3');
  });

  function surfaceError(unauthorised: boolean): void {
    const message = unauthorised
      ? 'Your session expired. Please sign in again.'
      : "We couldn't save your ship-from address. Please try again.";
    toast.error(message);
    setTopError(message);
  }

  const isSubmitting = form.formState.isSubmitting;
  const errors = form.formState.errors;

  return (
    <OnboardingShell>
      <StepIndicator currentStep={2} />
      <Card>
        <CardHeader>
          <CardTitle>Where do you ship from?</CardTitle>
          <CardDescription>
            We print this on every label as the return address and use it to route to your nearest
            Royal Mail collection point.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {topError ? (
            <Alert variant="destructive">
              <AlertDescription>{topError}</AlertDescription>
            </Alert>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="step2-line1">Address line 1</Label>
            <Input
              id="step2-line1"
              autoComplete="address-line1"
              aria-invalid={errors.line1 ? 'true' : undefined}
              className={cn(errors.line1 && 'border-destructive')}
              {...form.register('line1')}
            />
            {errors.line1 ? (
              <p className="text-destructive text-xs">{errors.line1.message}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="step2-line2">
              Address line 2 <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="step2-line2"
              autoComplete="address-line2"
              aria-invalid={errors.line2 ? 'true' : undefined}
              className={cn(errors.line2 && 'border-destructive')}
              {...form.register('line2')}
            />
            {errors.line2 ? (
              <p className="text-destructive text-xs">{errors.line2.message}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="step2-city">City</Label>
              <Input
                id="step2-city"
                autoComplete="address-level2"
                aria-invalid={errors.city ? 'true' : undefined}
                className={cn(errors.city && 'border-destructive')}
                {...form.register('city')}
              />
              {errors.city ? (
                <p className="text-destructive text-xs">{errors.city.message}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="step2-state">
                State / region <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="step2-state"
                autoComplete="address-level1"
                aria-invalid={errors.state ? 'true' : undefined}
                className={cn(errors.state && 'border-destructive')}
                {...form.register('state')}
              />
              {errors.state ? (
                <p className="text-destructive text-xs">{errors.state.message}</p>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="step2-postal-code">Postal code</Label>
              <Input
                id="step2-postal-code"
                autoComplete="postal-code"
                aria-invalid={errors.postal_code ? 'true' : undefined}
                className={cn(errors.postal_code && 'border-destructive')}
                {...form.register('postal_code')}
              />
              {errors.postal_code ? (
                <p className="text-destructive text-xs">{errors.postal_code.message}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="step2-country">Country</Label>
              <Controller
                control={form.control}
                name="country"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="step2-country" className="w-full">
                      <SelectValue placeholder="Select a country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          <span className="flex items-center gap-2.5">
                            <country.Flag
                              title={country.name}
                              style={{ width: 21, height: 14 }}
                              className="rounded-xs"
                            />
                            <span>{country.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          <div className="flex justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                void navigate('/app/onboarding/step/1');
              }}
            >
              Back
            </Button>
            <Button
              type="button"
              onClick={() => {
                void submit();
              }}
              disabled={isSubmitting || !form.formState.isValid}
            >
              {isSubmitting ? 'Saving…' : 'Save & continue'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </OnboardingShell>
  );
}
